import { useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/authService';

/**
 * Hook for automatic token refresh based on user activity.
 *
 * Refreshes the access token periodically while the user is active,
 * preventing session expiry during active use. This implements a
 * "sliding session" pattern where the session extends as long as
 * the user interacts with the application.
 *
 * @param refreshIntervalMs - How often to refresh the token (default: 5 minutes)
 * @param inactivityTimeoutMs - How long to wait after last activity before stopping refresh (default: 25 minutes)
 */
export function useTokenRefresh(
  refreshIntervalMs: number = 5 * 60 * 1000, // 5 minutes
  inactivityTimeoutMs: number = 25 * 60 * 1000 // 25 minutes (just before 30 min expiry)
) {
  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const refreshTokenIfActive = useCallback(async () => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;

    // Only refresh if user has been active within the inactivity timeout
    if (timeSinceLastActivity < inactivityTimeoutMs) {
      try {
        await authService.refreshToken();
      } catch (error) {
        // Token refresh failed - user will be logged out on next API call
        // Don't log to console to avoid noise
      }
    }
  }, [inactivityTimeoutMs]);

  useEffect(() => {
    // Only run if user is authenticated
    if (!authService.isAuthenticated()) {
      return;
    }

    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Set up periodic token refresh
    refreshIntervalRef.current = setInterval(refreshTokenIfActive, refreshIntervalMs);

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateLastActivity);
      });
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [refreshIntervalMs, updateLastActivity, refreshTokenIfActive]);
}
